import { Header } from "@/components/layout/Header";
import { Sidebar } from "@/components/layout/Sidebar";
import { MobileBottomNav } from "@/components/layout/MobileBottomNav";
import { HeroSection } from "@/components/home/HeroSection";
import { TrendingSection } from "@/components/home/TrendingSection";
import { FeaturesSection } from "@/components/home/FeaturesSection";
import { Logo } from "@/components/layout/Logo";
import { ShareButtons } from "@/components/social/ShareButtons";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Users,
  Trophy,
  Radio,
  ArrowRight,
  Zap,
  Shield,
  TrendingUp,
  Star,
  MessageSquare
} from "lucide-react";

const Index = () => {
  const { data: liveStreams } = useQuery({
    queryKey: ["live-streams-home"],
    queryFn: async () => {
      const { data } = await supabase
        .from("live_streams")
        .select("*, streamers(*)")
        .eq("is_live", true)
        .limit(3);
      return data ?? [];
    },
  });

  const { data: topTraders } = useQuery({
    queryKey: ["top-traders-home"],
    queryFn: async () => {
      const { data } = await supabase
        .from("leaderboard_entries")
        .select("*")
        .order("total_profit", { ascending: false })
        .limit(5);
      return data ?? [];
    },
  });

  return (
    <>
      <Helmet>
        <title>TheMoonRun - Solana Meme Coin Launchpad</title>
        <meta name="description" content="The ultimate Solana meme coin launchpad. Fair launch with bonding curves, no presales, and instant trading. Your community, your rules." />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://themoonrun.app/" />
        <meta property="og:title" content="TheMoonRun - Solana Meme Coin Launchpad" />
        <meta property="og:description" content="Launch your meme coin instantly on Solana. No presales, fair bonding curves." />
        <meta property="og:image" content="https://themoonrun.app/og-image.png" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://themoonrun.app/" />
        <meta property="twitter:title" content="TheMoonRun - Solana Meme Coin Launchpad" />
        <meta property="twitter:description" content="Launch your meme coin instantly on Solana. No presales, fair bonding curves." />
        <meta property="twitter:image" content="https://themoonrun.app/og-image.png" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <div className="flex">
          <Sidebar />

          <main className="flex-1 min-w-0 pb-20 lg:pb-0">
            <HeroSection />
            <TrendingSection />

            {/* Live Streams Section */}
            {liveStreams && liveStreams.length > 0 && (
              <section className="py-8 border-t border-border">
                <div className="container">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
                        <Radio className="w-5 h-5 text-red-500" />
                      </div>
                      <div>
                        <h2 className="font-display text-xl font-bold text-foreground">Live Now</h2>
                        <p className="text-sm text-muted-foreground">Streamers trading live</p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/streams">
                        View All <ArrowRight className="w-4 h-4 ml-1" />
                      </Link>
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {liveStreams.map((stream: any) => (
                      <div key={stream.id} className="p-4 rounded-xl bg-card border border-border hover:border-primary/50 transition-colors">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-primary-foreground font-bold">
                            {stream.streamers?.display_name?.charAt(0) || "S"}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-foreground truncate">{stream.streamers?.display_name || "Streamer"}</p>
                            <div className="flex items-center gap-2">
                              <Badge variant="destructive" className="text-[10px]">LIVE</Badge>
                              <span className="text-xs text-muted-foreground">{stream.viewer_count || 0} viewers</span>
                            </div>
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground truncate">{stream.title}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Top Traders Section */}
            {topTraders && topTraders.length > 0 && (
              <section className="py-8 border-t border-border">
                <div className="container">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                        <Trophy className="w-5 h-5 text-yellow-500" />
                      </div>
                      <div>
                        <h2 className="font-display text-xl font-bold text-foreground">Top Traders</h2>
                        <p className="text-sm text-muted-foreground">Copy the best performers</p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/leaderboard">
                        Leaderboard <ArrowRight className="w-4 h-4 ml-1" />
                      </Link>
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                    {topTraders.map((trader: any, idx: number) => (
                      <div key={trader.id} className="p-4 rounded-xl bg-card border border-border hover:border-primary/50 transition-colors text-center">
                        <div className="flex items-center justify-center mb-2">
                          {idx === 0 && <span className="text-2xl">🥇</span>}
                          {idx === 1 && <span className="text-2xl">🥈</span>}
                          {idx === 2 && <span className="text-2xl">🥉</span>}
                          {idx > 2 && <span className="text-lg text-muted-foreground">#{idx + 1}</span>}
                        </div>
                        <p className="font-mono text-sm text-foreground mb-1">
                          {trader.wallet_address?.slice(0, 4)}...{trader.wallet_address?.slice(-4)}
                        </p>
                        <p className="text-sm font-semibold text-success">
                          +{(trader.total_profit || 0).toFixed(2)} SOL
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {(trader.win_rate || 0).toFixed(0)}% win rate
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Quick Links Section */}
            <section className="py-8 border-t border-border">
              <div className="container">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Link to="/community-hub" className="p-6 rounded-xl bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/20 hover:border-blue-500/40 transition-colors group">
                    <MessageSquare className="w-8 h-8 text-blue-500 mb-3 group-hover:scale-110 transition-transform" />
                    <h3 className="font-semibold text-foreground mb-1">Community Hub</h3>
                    <p className="text-sm text-muted-foreground">Join the conversation</p>
                  </Link>
                  <Link to="/copy-subscriptions" className="p-6 rounded-xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 hover:border-green-500/40 transition-colors group">
                    <Users className="w-8 h-8 text-green-500 mb-3 group-hover:scale-110 transition-transform" />
                    <h3 className="font-semibold text-foreground mb-1">Copy Trading</h3>
                    <p className="text-sm text-muted-foreground">Follow top traders</p>
                  </Link>
                  <Link to="/marketplace" className="p-6 rounded-xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 hover:border-purple-500/40 transition-colors group">
                    <Star className="w-8 h-8 text-purple-500 mb-3 group-hover:scale-110 transition-transform" />
                    <h3 className="font-semibold text-foreground mb-1">Marketplace</h3>
                    <p className="text-sm text-muted-foreground">Get badges & cosmetics</p>
                  </Link>
                  <Link to="/streamer" className="p-6 rounded-xl bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/20 hover:border-orange-500/40 transition-colors group">
                    <Radio className="w-8 h-8 text-orange-500 mb-3 group-hover:scale-110 transition-transform" />
                    <h3 className="font-semibold text-foreground mb-1">Go Live</h3>
                    <p className="text-sm text-muted-foreground">Stream & earn</p>
                  </Link>
                </div>
              </div>
            </section>

            <FeaturesSection />

            {/* Why TheMoonRun Section */}
            <section className="py-12 border-t border-border bg-gradient-to-b from-transparent to-primary/5">
              <div className="container">
                <div className="text-center mb-10">
                  <h2 className="font-display text-3xl font-bold text-foreground mb-3">Why TheMoonRun?</h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto">
                    The most complete meme coin platform on Solana. Lower fees, more features, better community.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="p-6 rounded-xl bg-card border border-border">
                    <Zap className="w-10 h-10 text-primary mb-4" />
                    <h3 className="font-semibold text-foreground mb-2">Lowest Fees</h3>
                    <p className="text-sm text-muted-foreground">
                      0.02 SOL token creation - 60% cheaper than competitors. 0.25% trading fees with priority options.
                    </p>
                  </div>
                  <div className="p-6 rounded-xl bg-card border border-border">
                    <Shield className="w-10 h-10 text-primary mb-4" />
                    <h3 className="font-semibold text-foreground mb-2">100% Fair Launch</h3>
                    <p className="text-sm text-muted-foreground">
                      No presales, no insider advantages. Bonding curve ensures everyone gets fair pricing.
                    </p>
                  </div>
                  <div className="p-6 rounded-xl bg-card border border-border">
                    <TrendingUp className="w-10 h-10 text-primary mb-4" />
                    <h3 className="font-semibold text-foreground mb-2">Earn While Trading</h3>
                    <p className="text-sm text-muted-foreground">
                      Copy trading subscriptions, streamer referrals, community challenges - multiple ways to earn.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Share CTA Section */}
            <section className="py-8 border-t border-border">
              <div className="container">
                <div className="p-6 rounded-xl bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20 text-center">
                  <h3 className="font-display text-xl font-bold text-foreground mb-2">
                    Love TheMoonRun? Share it! 🚀
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Help us grow and earn referral rewards
                  </p>
                  <div className="flex justify-center">
                    <ShareButtons
                      title="TheMoonRun - The best Solana meme coin launchpad! 🚀🌙 Lower fees, fair launches, and amazing community."
                      url={window.location.origin}
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-border py-8 mt-8">
              <div className="container">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
                  <Logo size="sm" />
                  <div className="flex items-center gap-6 flex-wrap justify-center">
                    <Link to="/terms" className="hover:text-primary transition-colors">Terms</Link>
                    <Link to="/docs" className="hover:text-primary transition-colors">Docs</Link>
                    <Link to="/support" className="hover:text-primary transition-colors">Support</Link>
                    <Link to="/api" className="hover:text-primary transition-colors">API</Link>
                    <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">Twitter</a>
                    <a href="https://discord.com" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">Discord</a>
                  </div>
                </div>
                <p className="text-center text-xs text-muted-foreground mt-4">© 2025 TheMoonRun. All rights reserved.</p>
              </div>
            </footer>
          </main>
        </div>

        <MobileBottomNav />
      </div>
    </>
  );
};

export default Index;
